import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';

const PLAYERS_COLLECTION = 'players';
const PLAYER_RATINGS_COLLECTION = 'playerRatings';
const APP_META_COLLECTION = 'appMeta';
const SYNC_META_DOC = 'fantasySync';
const SYNC_LOCK_DOC = 'fantasySyncLock';
const PREVIOUS_SEASON = 2025;

const CACHE_KEY = 'fantapp_sync_cache_v2';
const PLAYERS_CACHE_KEY = 'fantapp_players_cache_v1';
const CACHE_TTL_MS = 8 * 60 * 60 * 1000; // 8 ore (~2-3 sync automatiche al giorno)

function normalizeText(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '');
}

function buildLookupKeys(player) {
  const name = normalizeText(player?.name || '');
  const role = normalizeText(player?.role || '');
  const keys = [];

  if (name && role) keys.push(`${name}|${role}`);
  if (name) keys.push(name);

  return keys;
}

function safeNow() {
  return Date.now();
}

// Scrive/cancella molti documenti in batch (max 500 operazioni per batch
// Firestore, qui 400 per stare larghi) invece di centinaia di setDoc/deleteDoc
// in parallelo: molto più veloce e, soprattutto, molto meno soggetto a restare
// "appeso" quando la connettività è degradata (un batch pendente è UNA promise
// sola da gestire, non 600).
async function commitInChunks(ops, chunkSize = 400) {
  for (let i = 0; i < ops.length; i += chunkSize) {
    const batch = writeBatch(db);
    ops.slice(i, i + chunkSize).forEach((op) => {
      if (op.type === 'delete') batch.delete(op.ref);
      else batch.set(op.ref, op.data, { merge: true });
    });
    await batch.commit();
  }
}

function readCachedSyncMeta() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedSyncMeta(meta) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(meta));
  } catch {
    // ignore
  }
}

function isFreshMeta(meta) {
  if (!meta?.lastSyncedAt) return false;
  return safeNow() - Number(meta.lastSyncedAt) < CACHE_TTL_MS;
}

const FETCH_TIMEOUT_MS = 8000;

async function fetchJson(url, timeoutMs = FETCH_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) {
      throw new Error(`Errore nel recupero dati da ${url}`);
    }
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

function createEmptyRating(day) {
  return {
    day,
    mark: 0,
    fmark: 0,
    penalty: 0,
    team: '',
    id: null,
    assist: 0,
    spenalty: 0,
    rcard: 0,
    exit: false,
    mpenalty: 0,
    owngoal: 0,
    drawgoal: 0,
    wingoal: 0,
    name: '',
    enter: false,
    gotgoal: 0,
    goal: 0,
    role: '',
    ycard: 0,
  };
}

function sanitizePreviousSeasonPlayer(player) {
  return {
    season: PREVIOUS_SEASON,
    id: player.id ?? null,
    team: player.team || '',
    playmaker: Boolean(player.playmaker),
    value: Number(player.value || 0),
    gotgoals: Number(player.gotgoals || 0),
    goals: Number(player.goals || 0),
    markavg: Number(player.markavg || 0),
    owngoals: Number(player.owngoals || 0),
    ycards: Number(player.ycards || 0),
    name: player.name || '',
    role: player.role || '',
    spenalties: Number(player.spenalties || 0),
    fmarkavg: Number(player.fmarkavg || 0),
    assists: Number(player.assists || 0),
    penalties: Number(player.penalties || 0),
    rcards: Number(player.rcards || 0),
    caps: Number(player.caps || 0),
    mpenalties: Number(player.mpenalties || 0),
    last5: {
      ycards: Number(player.last5?.ycards || 0),
      gotgoals: Number(player.last5?.gotgoals || 0),
      goals: Number(player.last5?.goals || 0),
      markavg: Number(player.last5?.markavg || 0),
      assists: Number(player.last5?.assists || 0),
      spenalties: Number(player.last5?.spenalties || 0),
      fmarkavg: Number(player.last5?.fmarkavg || 0),
      owngoals: Number(player.last5?.owngoals || 0),
      penalties: Number(player.last5?.penalties || 0),
      rcards: Number(player.last5?.rcards || 0),
      caps: Number(player.last5?.caps || 0),
      mpenalties: Number(player.last5?.mpenalties || 0),
    },
    syncedAt: safeNow(),
  };
}

function buildPreviousSeasonIndex(players) {
  const index = new Map();

  for (const player of players) {
    const payload = sanitizePreviousSeasonPlayer(player);

    for (const key of buildLookupKeys(player)) {
      if (!index.has(key)) {
        index.set(key, payload);
      }
    }
  }

  return index;
}

function attachPreviousSeasonStats(currentPlayers, previousSeasonIndex) {
  return currentPlayers.map((player) => {
    let previousSeasonStats = null;

    for (const key of buildLookupKeys(player)) {
      if (previousSeasonIndex.has(key)) {
        previousSeasonStats = previousSeasonIndex.get(key);
        break;
      }
    }

    return {
      ...player,
      previousSeasonStats: previousSeasonStats || null,
      previousSeasonSyncedAt: previousSeasonStats?.syncedAt || null,
      previousSeason: PREVIOUS_SEASON,
      updatedAt: safeNow(),
    };
  });
}

function buildSyncMeta({
  playersCount,
  previousSeasonPlayersCount,
  ratingsMaxDay,
}) {
  return {
    lastSyncedAt: safeNow(),
    playersCount: Number(playersCount || 0),
    previousSeasonPlayersCount: Number(previousSeasonPlayersCount || 0),
    ratingsMaxDay: Number(ratingsMaxDay || 0),
    previousSeason: PREVIOUS_SEASON,
    sourceVersion: 'playersstats+ratings+season2024',
    updatedAt: safeNow(),
  };
}

async function saveSyncMeta(meta) {
  await setDoc(
    doc(db, APP_META_COLLECTION, SYNC_META_DOC),
    meta,
    { merge: true }
  );
  writeCachedSyncMeta(meta);
}

// Legge il meta SEMPRE da Firestore (non dalla cache locale): è l'unico modo
// per accorgersi che un altro dispositivo ha appena sincronizzato.
async function fetchRemoteSyncMeta() {
  const snap = await getDoc(doc(db, APP_META_COLLECTION, SYNC_META_DOC));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function getSyncMetadata() {
  const local = readCachedSyncMeta();
  if (local) return local;

  const snap = await getDoc(doc(db, APP_META_COLLECTION, SYNC_META_DOC));
  if (!snap.exists()) return null;

  const meta = { id: snap.id, ...snap.data() };
  writeCachedSyncMeta(meta);
  return meta;
}

export async function fetchPlayersFromApi(season = null) {
  const url = season
    ? `https://apicdn.fantamaster.it/playersstats/?season=${season}`
    : 'https://apicdn.fantamaster.it/playersstats/';

  const data = await fetchJson(url);
  return data.players || [];
}

export async function fetchRatingsDay(day) {
  const data = await fetchJson(`https://apicdn.fantamaster.it/ratings/?day=${day}`);
  return {
    maxday: Number(data.maxday || 0),
    ratings: data.ratings || [],
  };
}

export async function syncPlayersToFirestore(players = null, previousSeasonIndex = null) {
  const currentPlayers = players || await fetchPlayersFromApi();
  const payloads = previousSeasonIndex
    ? attachPreviousSeasonStats(currentPlayers, previousSeasonIndex)
    : currentPlayers.map((player) => ({ ...player, updatedAt: safeNow() }));

  const currentIds = new Set(payloads.map((player) => String(player.id)));

  const existingSnap = await getDocs(collection(db, PLAYERS_COLLECTION));
  const staleIds = existingSnap.docs
    .map((d) => d.id)
    .filter((id) => !currentIds.has(id));

  await commitInChunks([
    ...payloads.map((player) => ({
      type: 'set',
      ref: doc(db, PLAYERS_COLLECTION, String(player.id)),
      data: player,
    })),
    ...staleIds.map((id) => ({
      type: 'delete',
      ref: doc(db, PLAYERS_COLLECTION, id),
    })),
  ]);

  // Invalida la cache locale dei giocatori
  try { localStorage.removeItem(PLAYERS_CACHE_KEY); } catch {}

  return payloads.length;
}

// Quante giornate scaricare in parallelo per volta: con un unico Promise.all
// su tutte le giornate le risposte arrivano quasi in blocco (la progress bar
// "scatterebbe" da 15% a 90% in un colpo solo). A lotti, invece, l'avanzamento
// resta visibile e graduale per tutta la durata reale del fetch.
const RATINGS_FETCH_BATCH_SIZE = 6;

export async function syncRatingsToFirestore({ onProgress } = {}) {
  const firstDay = await fetchRatingsDay(1);
  const maxDay = Number(firstDay.maxday) || 0;

  if (maxDay <= 0) {
    // Stagione non ancora iniziata (l'API riporta maxday: 0): non c'è niente
    // di reale da sincronizzare. Prima qui si ricadeva su un fallback fisso
    // di 38 giornate, che faceva comunque partire le richieste per i giorni
    // 2-38: essendo apicdn una CDN, quelle richieste potevano restituire
    // risposte cache stantie con i voti reali della stagione precedente,
    // sovrascrivendo Firestore con dati sbagliati. Meglio non toccare nulla
    // e lasciare quello che c'è già salvato.
    return { maxDay: 0, playersUpdated: 0 };
  }

  let completedDays = 1;
  onProgress?.(completedDays, maxDay);

  const dayPayloads = new Array(maxDay);
  dayPayloads[0] = { day: 1, ratings: firstDay.ratings || [] };

  const remainingDays = Array.from({ length: maxDay - 1 }, (_, index) => index + 2);

  for (let i = 0; i < remainingDays.length; i += RATINGS_FETCH_BATCH_SIZE) {
    const batch = remainingDays.slice(i, i + RATINGS_FETCH_BATCH_SIZE);

    await Promise.all(
      batch.map(async (day) => {
        const payload = await fetchRatingsDay(day);
        dayPayloads[day - 1] = { day, ratings: payload.ratings || [] };
      })
    );

    completedDays += batch.length;
    onProgress?.(completedDays, maxDay);
  }

  const byPlayer = new Map();

  for (const { day, ratings } of dayPayloads) {
    for (const rating of ratings) {
      const playerId = String(rating.id);

      if (!byPlayer.has(playerId)) {
        byPlayer.set(
          playerId,
          Array.from({ length: maxDay }, (_, index) => createEmptyRating(index + 1))
        );
      }

      byPlayer.get(playerId)[day - 1] = {
        day,
        mark: Number(rating.mark || 0),
        fmark: Number(rating.fmark || 0),
        penalty: Number(rating.penalty || 0),
        team: rating.team || '',
        id: rating.id,
        assist: Number(rating.assist || 0),
        spenalty: Number(rating.spenalty || 0),
        rcard: Number(rating.rcard || 0),
        exit: Boolean(rating.exit),
        mpenalty: Number(rating.mpenalty || 0),
        owngoal: Number(rating.owngoal || 0),
        drawgoal: Number(rating.drawgoal || 0),
        wingoal: Number(rating.wingoal || 0),
        name: rating.name || '',
        enter: Boolean(rating.enter),
        gotgoal: Number(rating.gotgoal || 0),
        goal: Number(rating.goal || 0),
        role: rating.role || '',
        ycard: Number(rating.ycard || 0),
      };
    }
  }

  // Chunk più piccolo per i voti: ogni doc porta l'intero storico giornata per
  // giornata, quindi si tiene il batch ben sotto il limite di 10 MiB.
  await commitInChunks(
    Array.from(byPlayer.entries()).map(([playerId, ratingsHistory]) => ({
      type: 'set',
      ref: doc(db, PLAYER_RATINGS_COLLECTION, playerId),
      data: { playerId, ratingsHistory, ratingsMaxDay: maxDay, updatedAt: safeNow() },
    })),
    150
  );

  return {
    maxDay,
    playersUpdated: byPlayer.size,
  };
}

export async function syncAllFantasyData() {
  setSyncProgress(5);

  const [currentPlayers, previousSeasonPlayers] = await Promise.all([
    fetchPlayersFromApi(),
    fetchPlayersFromApi(PREVIOUS_SEASON),
  ]);

  setSyncProgress(15);

  const previousSeasonIndex = buildPreviousSeasonIndex(previousSeasonPlayers);

  const [playersCount, ratingsResult] = await Promise.all([
    syncPlayersToFirestore(currentPlayers, previousSeasonIndex),
    syncRatingsToFirestore({
      onProgress: (completedDays, maxDay) => {
        // I giorni di voti fetchati sono di gran lunga il passo più lento:
        // gli si riserva la fascia 15-90% della barra. maxDay può essere 0
        // se non c'è ancora nessuna giornata (fuori stagione).
        if (maxDay > 0) setSyncProgress(15 + (completedDays / maxDay) * 75);
      },
    }),
  ]);

  setSyncProgress(95);

  const meta = buildSyncMeta({
    playersCount,
    previousSeasonPlayersCount: previousSeasonPlayers.length,
    ratingsMaxDay: ratingsResult.maxDay,
  });

  await saveSyncMeta(meta);

  setSyncProgress(100);

  return {
    playersSynced: currentPlayers.length,
    previousSeasonPlayersSynced: previousSeasonPlayers.length,
    previousSeason: PREVIOUS_SEASON,
    meta,
  };
}

let syncLock = null;

// ─── Stato reattivo della sincronizzazione ───────────────────────────────────
// Vero solo mentre è in corso la sincronizzazione "pesante" (fetch API + scrittura
// su Firestore), non quando viene saltata perché la cache è già aggiornata.
// `source` dice CHI la sta facendo: 'local' (questo dispositivo) o 'remote'
// (un altro dispositivo: qui si aspetta e basta, vedi waitForRemoteSync).
// La UI può iscriversi per avvisare l'utente di attendere.
let syncInProgress = false;
let syncProgress = 0;
let syncSource = 'local';
const syncStatusListeners = new Set();

function getSyncSnapshot() {
  return { syncing: syncInProgress, progress: syncProgress, source: syncSource };
}

function notifySyncListeners() {
  const snapshot = getSyncSnapshot();
  syncStatusListeners.forEach((cb) => {
    try { cb(snapshot); } catch {}
  });
}

function setSyncInProgress(value, source = 'local') {
  if (syncInProgress === value && syncSource === source) return;
  syncInProgress = value;
  syncSource = source;
  if (!value) syncProgress = 0;
  notifySyncListeners();
}

// Chiamato ad ogni avanzamento della sync locale: aggiorna il "lucchetto"
// condiviso (heartbeat + percentuale) così gli altri dispositivi vedono la
// stessa barra. Impostato solo mentre questo dispositivo detiene il lucchetto.
let progressReporter = null;

function setSyncProgress(value) {
  const clamped = Math.max(0, Math.min(100, value));
  if (clamped === syncProgress) return;
  syncProgress = clamped;
  notifySyncListeners();
  if (progressReporter) progressReporter(clamped);
}

export function isFantasyDataSyncing() {
  return syncInProgress;
}

export function subscribeFantasyDataSync(callback) {
  if (typeof callback !== 'function') return () => {};
  syncStatusListeners.add(callback);
  try { callback(getSyncSnapshot()); } catch {}
  return () => { syncStatusListeners.delete(callback); };
}

// Tetto di durata assoluto per la sincronizzazione "pesante". Serve come rete
// di sicurezza: le scritture Firestore (`setDoc`) NON vengono mai rifiutate
// quando il client non riesce a raggiungere il backend (connessione assente o
// degradata) — restano semplicemente pendenti a tempo indeterminato. Senza
// questo tetto un blip di rete all'avvio dell'app lascerebbe `syncInProgress`
// a true per sempre: banner "Sincronizzazione in corso…" bloccato e sezioni
// Aste/Strategie/Simula rosa irraggiungibili (vedi AuctionsGuard). Allo
// scadere del tempo si sblocca comunque la UI; le scritture già applicate in
// locale verranno propagate da Firestore quando torna la connettività, e al
// prossimo avvio la sync riparte (il meta non è stato salvato).
const SYNC_HARD_TIMEOUT_MS = 90 * 1000;

// ─── Coordinamento tra dispositivi ───────────────────────────────────────────
// I dati sincronizzati sono globali (players/playerRatings/appMeta), quindi se
// due dispositivi si aprono insieme (es. PC e telefono) basta che UNO li
// sincronizzi: l'altro aspetta e poi legge da Firestore. Il coordinamento passa
// da un "lucchetto" in appMeta/fantasySyncLock preso in transazione:
//  - `startedAt`: quando è partita la sync (il lucchetto scade comunque dopo
//    SYNC_LEASE_MAX_MS, così un dispositivo chiuso a metà non blocca nessuno);
//  - `heartbeatAt`: aggiornato con l'avanzamento — se si ferma per più di
//    SYNC_HEARTBEAT_STALE_MS il lucchetto è considerato abbandonato.
// I tempi sono orologi dei client: dispositivi con l'ora presa dalla rete
// (praticamente tutti) bastano per finestre di minuti come queste.
const SYNC_LEASE_MAX_MS = SYNC_HARD_TIMEOUT_MS + 30 * 1000;
const SYNC_HEARTBEAT_STALE_MS = 45 * 1000;
const HEARTBEAT_MIN_INTERVAL_MS = 4 * 1000;
const MAX_LEASE_ATTEMPTS = 3;
const DEVICE_ID = `${safeNow().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

function isLeaseAlive(lock, now = safeNow()) {
  if (!lock?.owner || !lock.startedAt) return false;
  if (now - Number(lock.startedAt) > SYNC_LEASE_MAX_MS) return false;
  const heartbeat = Number(lock.heartbeatAt || lock.startedAt);
  return now - heartbeat < SYNC_HEARTBEAT_STALE_MS;
}

// true se questo dispositivo ha preso il lucchetto, false se un altro sta già
// sincronizzando. Se la transazione fallisce (permessi, rete) si sincronizza
// comunque in locale: meglio una sync doppia che nessuna sync.
async function claimSyncLease() {
  const lockRef = doc(db, APP_META_COLLECTION, SYNC_LOCK_DOC);
  try {
    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(lockRef);
      const lock = snap.exists() ? snap.data() : null;
      if (isLeaseAlive(lock) && lock.owner !== DEVICE_ID) return false;
      const now = safeNow();
      tx.set(lockRef, { owner: DEVICE_ID, startedAt: now, heartbeatAt: now, progress: 0, finishedAt: null });
      return true;
    });
  } catch (error) {
    console.warn('Lucchetto sync non disponibile, procedo in locale:', error);
    return true;
  }
}

function startLeaseHeartbeat() {
  const lockRef = doc(db, APP_META_COLLECTION, SYNC_LOCK_DOC);
  let lastSentAt = 0;
  let lastSentProgress = -1;
  progressReporter = (progress) => {
    const now = safeNow();
    if (progress < 100 && now - lastSentAt < HEARTBEAT_MIN_INTERVAL_MS && progress - lastSentProgress < 10) return;
    lastSentAt = now;
    lastSentProgress = progress;
    setDoc(lockRef, { heartbeatAt: now, progress: Math.round(progress) }, { merge: true }).catch(() => {});
  };
  return () => { progressReporter = null; };
}

function releaseSyncLease({ success }) {
  const lockRef = doc(db, APP_META_COLLECTION, SYNC_LOCK_DOC);
  setDoc(lockRef, { owner: null, finishedAt: safeNow(), success: Boolean(success) }, { merge: true }).catch(() => {});
}

// Un altro dispositivo sta sincronizzando: si ascoltano lucchetto e meta e si
// rispecchia la sua barra di avanzamento. Esito:
//  - 'done'  → il meta su Firestore è stato aggiornato dopo l'inizio della sua
//              sync: i dati sono pronti, nessuna sync locale;
//  - 'retry' → lucchetto rilasciato senza successo o scaduto (dispositivo
//              chiuso a metà): si riprova a prenderlo noi.
function waitForRemoteSync() {
  return new Promise((resolve) => {
    const lockRef = doc(db, APP_META_COLLECTION, SYNC_LOCK_DOC);
    const metaRef = doc(db, APP_META_COLLECTION, SYNC_META_DOC);
    let lock = null;
    let latestMeta = null;
    let settled = false;
    let staleCheck = null;
    const unsubs = [];

    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearInterval(staleCheck);
      unsubs.forEach((u) => u());
      resolve(result);
    };

    const isDoneMeta = (meta) => {
      const startedAt = Number(lock?.startedAt || 0);
      return isFreshMeta(meta) && startedAt > 0 && Number(meta.lastSyncedAt) >= startedAt;
    };

    // Lucchetto e meta sono due documenti diversi: lo snapshot del lucchetto
    // rilasciato può arrivare PRIMA di quello del meta aggiornato. Quando il
    // lucchetto non è più valido si rilegge quindi il meta da Firestore prima
    // di concludere che l'altro dispositivo ha fallito.
    const evaluate = () => {
      if (settled || !lock) return;
      if (isDoneMeta(latestMeta)) { finish({ status: 'done', meta: latestMeta }); return; }
      if (isLeaseAlive(lock)) return;
      fetchRemoteSyncMeta()
        .then((meta) => finish(isDoneMeta(meta) ? { status: 'done', meta } : { status: 'retry' }))
        .catch(() => finish({ status: 'retry' }));
    };

    unsubs.push(onSnapshot(lockRef, (snap) => {
      lock = snap.exists() ? snap.data() : null;
      if (lock?.progress != null) setSyncProgress(Number(lock.progress));
      evaluate();
    }, () => finish({ status: 'retry' })));

    unsubs.push(onSnapshot(metaRef, (snap) => {
      if (!snap.exists()) return;
      latestMeta = { id: snap.id, ...snap.data() };
      evaluate();
    }, () => {}));

    // Un heartbeat che smette di arrivare non genera snapshot: lo si controlla
    // anche a intervalli, altrimenti si aspetterebbe all'infinito.
    staleCheck = setInterval(evaluate, 5000);
  });
}

export async function ensureFantasyDataSynced({ force = false } = {}) {
  if (syncLock) {
    return syncLock;
  }

  syncLock = (async () => {
    if (!force) {
      const localMeta = readCachedSyncMeta();

      if (isFreshMeta(localMeta)) {
        return {
          skipped: true,
          reason: 'local-cache',
          meta: localMeta,
        };
      }

      // App offline: non c'è modo di sincronizzare né di sapere se i dati
      // remoti sono più freschi. Si usa quello che c'è già in cache/Firestore
      // e non si mostra nemmeno il banner (setSyncInProgress resta false).
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return {
          skipped: true,
          reason: 'offline',
          meta: localMeta || null,
        };
      }

      // Meta letto da Firestore, NON dalla cache locale (getSyncMetadata
      // restituirebbe il meta locale, vecchio): è così che ci si accorge che
      // un altro dispositivo ha sincronizzato da poco, e non si rifà tutto.
      const remoteMeta = await fetchRemoteSyncMeta().catch(() => null);
      if (isFreshMeta(remoteMeta)) {
        writeCachedSyncMeta(remoteMeta);
        return {
          skipped: true,
          reason: 'remote-cache',
          meta: remoteMeta,
        };
      }
    }

    // Prima di partire: se un altro dispositivo sta già sincronizzando, si
    // aspetta lui (stessa barra di avanzamento) invece di fare il lavoro due volte.
    for (let attempt = 0; attempt < MAX_LEASE_ATTEMPTS; attempt++) {
      // eslint-disable-next-line no-await-in-loop
      const acquired = await claimSyncLease();
      if (acquired) break;

      setSyncInProgress(true, 'remote');
      // eslint-disable-next-line no-await-in-loop
      const outcome = await waitForRemoteSync();
      if (outcome.status === 'done') {
        writeCachedSyncMeta(outcome.meta);
        return {
          skipped: true,
          reason: 'remote-sync',
          meta: outcome.meta,
        };
      }
    }

    setSyncInProgress(true, 'local');
    setSyncProgress(0);
    const stopHeartbeat = startLeaseHeartbeat();

    // Rete di sicurezza: se la sync non si conclude entro il tetto massimo
    // (tipicamente perché una scrittura Firestore resta pendente per problemi
    // di connettività) si sblocca comunque la UI. `syncAllFantasyData`
    // continua in background: le sue scritture verranno propagate più tardi.
    let timeoutId = null;
    const timeout = new Promise((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error('Sincronizzazione interrotta: tempo massimo superato')),
        SYNC_HARD_TIMEOUT_MS
      );
    });

    let success = false;
    try {
      const result = await Promise.race([syncAllFantasyData(), timeout]);
      success = true;
      return result;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      stopHeartbeat();
      releaseSyncLease({ success });
    }
  })().finally(() => {
    syncLock = null;
    setSyncInProgress(false);
  });

  return syncLock;
}

export async function getPlayersFromFirestore() {
  // Prova la cache localStorage prima di andare su Firestore
  try {
    const raw = localStorage.getItem(PLAYERS_CACHE_KEY);
    if (raw) {
      const { players, cachedAt } = JSON.parse(raw);
      // Cache valida solo se più recente dell'ultima sync nota: se un altro
      // dispositivo ha sincronizzato dopo, si rilegge da Firestore.
      const lastSyncedAt = Number(readCachedSyncMeta()?.lastSyncedAt || 0);
      if (Array.isArray(players) && players.length > 0 && Date.now() - cachedAt < CACHE_TTL_MS && cachedAt >= lastSyncedAt) {
        return players;
      }
    }
  } catch {}

  const q = query(collection(db, PLAYERS_COLLECTION), orderBy('name', 'asc'));
  const snap = await getDocs(q);
  const players = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Salva in cache
  try {
    localStorage.setItem(PLAYERS_CACHE_KEY, JSON.stringify({ players, cachedAt: Date.now() }));
  } catch {}

  return players;
}

export async function getPlayerById(playerId) {
  const [playerSnap, ratingsSnap] = await Promise.all([
    getDoc(doc(db, PLAYERS_COLLECTION, String(playerId))),
    getDoc(doc(db, PLAYER_RATINGS_COLLECTION, String(playerId))),
  ]);

  if (!playerSnap.exists() && !ratingsSnap.exists()) return null;

  return {
    id: String(playerId),
    ...(playerSnap.exists() ? playerSnap.data() : {}),
    ...(ratingsSnap.exists() ? ratingsSnap.data() : {}),
  };
}

// Recupera l'intera collection playerRatings in un'unica query, invece di un
// getDoc per giocatore: utile per analisi che devono guardare i voti storici
// di molti giocatori insieme (es. forma degli svincolati).
export async function getAllPlayerRatingsMap() {
  const snap = await getDocs(collection(db, PLAYER_RATINGS_COLLECTION));
  const map = new Map();
  snap.docs.forEach((d) => { map.set(d.id, d.data()); });
  return map;
}

export function buildSeasonTrendSeries(player) {
  const maxDay = Number(player?.ratingsMaxDay || player?.ratingsHistory?.length || 0);
  const history = Array.isArray(player?.ratingsHistory) ? player.ratingsHistory : [];

  if (!maxDay) return [];

  const byDay = new Map(history.map((entry) => [Number(entry.day), entry]));

  return Array.from({ length: maxDay }, (_, index) => {
    const day = index + 1;
    const entry = byDay.get(day);

    return {
      day,
      mark: Number(entry?.mark || 0),
      fmark: Number(entry?.fmark || 0),
      goal: Number(entry?.goal || 0),
      assist: Number(entry?.assist || 0),
      gotgoal: Number(entry?.gotgoal || 0),
      ycard: Number(entry?.ycard || 0),
      rcard: Number(entry?.rcard || 0),
      owngoal: Number(entry?.owngoal || 0),
      penalty: Number(entry?.penalty || 0),
      spenalty: Number(entry?.spenalty || 0),
      mpenalty: Number(entry?.mpenalty || 0),
      enter: Boolean(entry?.enter),
      exit: Boolean(entry?.exit),
    };
  });
}

export function buildRunningAverageSeries(player) {
  const series = buildSeasonTrendSeries(player);
  let sumMark = 0;
  let sumFmark = 0;

  return series.map((row, index) => {
    sumMark += row.mark;
    sumFmark += row.fmark;
    const dayCount = index + 1;

    return {
      ...row,
      avgMark: Number((sumMark / dayCount).toFixed(2)),
      avgFmark: Number((sumFmark / dayCount).toFixed(2)),
    };
  });
}

// ─── Probabilità di impiego (probabili formazioni) ───────────────────────────
// Fonte: stesso CDN FantaMaster già usato sopra, endpoint non documentato
// /lineups/. Per ogni partita della giornata corrente contiene, per la fonte
// "fm" (FantaMaster), la probabile formazione titolare + panchina con un
// campo "prob" per giocatore. Ad oggi "prob" distingue solo titolare (90) da
// panchina (30) — non è ancora una probabilità granulare per ogni giocatore,
// ma resta il segnale migliore disponibile gratis sullo stesso dominio.
const LINEUP_CACHE_KEY = 'fantapp_lineup_status_cache_v1';
const LINEUP_CACHE_TTL_MS = 3 * 60 * 60 * 1000; // 3 ore: dato di giornata, cambia più spesso delle stats di stagione

function readCachedLineupStatus() {
  try {
    const raw = localStorage.getItem(LINEUP_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.fetchedAt || safeNow() - Number(parsed.fetchedAt) >= LINEUP_CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCachedLineupStatus(payload) {
  try {
    localStorage.setItem(LINEUP_CACHE_KEY, JSON.stringify({ ...payload, fetchedAt: safeNow() }));
  } catch {
    // ignore
  }
}

async function fetchLineupStatusByName() {
  const cached = readCachedLineupStatus();
  if (cached) return cached;

  const data = await fetchJson('https://apicdn.fantamaster.it/lineups/');
  const byName = {};

  (data?.lineups || []).forEach((match) => {
    const source = match?.fm;
    if (!source) return;

    [
      ['home_lineup', 'starter'],
      ['away_lineup', 'starter'],
      ['home_bench', 'bench'],
      ['away_bench', 'bench'],
    ].forEach(([field, status]) => {
      (source[field] || []).forEach((entry) => {
        const key = normalizeText(entry?.player);
        if (!key) return;
        const prob = Number(entry?.prob);
        byName[key] = {
          status,
          prob: Number.isFinite(prob) ? prob : null,
        };
      });
    });
  });

  const payload = { byName, day: data?.day ?? null, lastUpdated: data?.last_updated || null };
  writeCachedLineupStatus(payload);
  return payload;
}

/**
 * Ricava, per ciascuno dei giocatori passati, lo stato di impiego previsto
 * per la prossima giornata (titolare/panchina/sconosciuto) indicizzato per
 * id giocatore. Non lancia mai eccezioni: in caso di errore ritorna una mappa
 * vuota, così l'UI può mostrare semplicemente "—" dove manca il dato.
 */
export async function getLineupStatusByPlayerId(players = []) {
  try {
    const { byName, day, lastUpdated } = await fetchLineupStatusByName();
    const byId = {};

    players.forEach((player) => {
      const key = normalizeText(player?.name);
      const entry = key ? byName[key] : null;
      if (entry) byId[String(player.id)] = entry;
    });

    return { byId, day, lastUpdated };
  } catch (err) {
    console.warn('[playerService] impossibile recuperare la probabilità di impiego', err);
    return { byId: {}, day: null, lastUpdated: null };
  }
}

export function buildLastFiveSeries(player) {
  return buildSeasonTrendSeries(player).slice(-5);
}